import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar, CalendarPlus, Clock, MapPin, Check, X, Ban } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import type { Appointment, AppointmentFormData } from "@/hooks/useAppointments";
import { generateGoogleCalendarUrl } from "@/hooks/useAppointments";
import AppointmentForm from "./AppointmentForm";

interface AppointmentCardProps {
  appointment: Appointment;
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  onCancel: (id: string) => void;
  onCounterPropose: (id: string, data: AppointmentFormData) => void;
  loading?: boolean;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  proposed:        { label: "En attente", variant: "secondary" },
  counterProposed: { label: "En attente", variant: "secondary" },
  accepted:        { label: "Confirmé",   variant: "default" },
  confirmed:       { label: "Confirmé",   variant: "default" },
  declined:        { label: "Archivé",    variant: "outline" },
  canceled:        { label: "Archivé",    variant: "outline" },
  expired:         { label: "Archivé",    variant: "outline" },
};

const AppointmentCard = ({ appointment, onAccept, onDecline, onCancel, onCounterPropose, loading }: AppointmentCardProps) => {
  const { user } = useAuth();
  const [showCounter, setShowCounter] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const apt = appointment;
  const config = statusConfig[apt.status] || statusConfig.proposed;

  const isClient = user?.id === apt.client_user_id;
  const isArtisan = user?.id === apt.artisan_user_id;

  const startDate = new Date(apt.starts_at);
  const shortId = `RDV-${apt.id.slice(0, 4).toUpperCase()}`;

  const locationLabel =
    apt.location_type === "call"
      ? "📞 Appel / Visio"
      : apt.location_type === "clientAddress"
        ? `🏠 ${apt.location_text || "Chez le client"}`
        : `📍 ${apt.location_text}`;

  // La partie qui n'a pas proposé peut accepter ou refuser.
  const canAct =
    apt.status === "proposed" &&
    ((isClient && apt.created_by === "artisan") || (isArtisan && apt.created_by === "client"));

  if (showCounter) {
    return (
      <AppointmentForm
        title="Proposer un autre créneau"
        onSubmit={(data) => {
          onCounterPropose(apt.id, data);
          setShowCounter(false);
        }}
        onCancel={() => setShowCounter(false)}
        loading={loading}
        initialData={{
          startsAt: startDate.toISOString().split("T")[0],
          startTime: startDate.toTimeString().slice(0, 5),
          durationMinutes: apt.duration_minutes,
          locationType: apt.location_type as any,
          locationText: apt.location_text,
        }}
      />
    );
  }

  return (
    <>
    <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Annuler ce rendez-vous ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le rendez-vous du {new Date(apt.starts_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {new Date(apt.starts_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} sera annulé. L'autre participant en sera notifié.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Garder le RDV</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => { onCancel(apt.id); setShowCancelConfirm(false); }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Annuler le RDV
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div className="border rounded-xl p-3 bg-card shadow-sm max-w-xs mx-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">{shortId}</span>
        <Badge variant={config.variant} className="text-[10px]">{config.label}</Badge>
      </div>

      <h4 className="font-display font-semibold text-sm mb-2 flex items-center gap-1.5">
        <Calendar className="h-3.5 w-3.5 text-primary" />
        Rendez-vous
      </h4>

      <div className="space-y-1.5 text-xs text-muted-foreground">
        <p className="flex items-center gap-1.5">
          <Calendar className="h-3 w-3 shrink-0" />
          {startDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
        <p className="flex items-center gap-1.5">
          <Clock className="h-3 w-3 shrink-0" />
          {startDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {apt.duration_minutes} min
        </p>
        <p className="flex items-center gap-1.5">
          <MapPin className="h-3 w-3 shrink-0" />
          {locationLabel}
        </p>
      </div>

      {apt.notes && (
        <p className="mt-2 text-xs italic text-muted-foreground border-t pt-2">
          {apt.notes}
        </p>
      )}

      {/* Actions */}
      <div className="mt-3 space-y-2">
        {canAct && (
          <div className="flex gap-1.5">
            <Button size="sm" variant="default" className="flex-1 text-xs h-8" onClick={() => onAccept(apt.id)} disabled={loading}>
              <Check className="h-3 w-3 mr-1" /> Accepter
            </Button>
            <Button size="sm" variant="destructive" className="flex-1 text-xs h-8" onClick={() => onDecline(apt.id)} disabled={loading}>
              <X className="h-3 w-3 mr-1" /> Refuser
            </Button>
          </div>
        )}

        {(apt.status === "accepted" || apt.status === "confirmed") && (
          <div className="space-y-1.5">
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs h-8"
                onClick={() => window.open(generateGoogleCalendarUrl(apt), "_blank")}
              >
                <CalendarPlus className="h-3 w-3 mr-1" /> Google
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-xs h-7 text-destructive hover:text-destructive"
              onClick={() => setShowCancelConfirm(true)}
              disabled={loading}
            >
              <Ban className="h-3 w-3 mr-1" /> Annuler le RDV
            </Button>
          </div>
        )}

        {apt.status === "expired" && (
          <p className="text-xs text-center text-muted-foreground">Proposition expirée</p>
        )}
      </div>
    </div>
    </>
  );
};

export default AppointmentCard;
